// Castintech dashboard — Credentials Expiry view (Faz 36 PR-A).
// Backend: GET /castintech-api/credentials/expiry-status (1 saat cache, admin-bearer).
// Görüntüler: days_remaining badge (yeşil/sarı/kırmızı) + status + manuel "Rotate now" hint.
//
// Tasarım: minimal — JSON view + rozet. Rotate butonu sadece manuel komut gösterir
// (otomatik rotation yapılmaz; `claude setup-token` PTY akışı kullanıcı kontrolünde).

import { authBearerFetch, ensureAdminBearer, clearAdminBearer } from '../auth.js';
import { renderAuthRequired, renderLoading, renderError } from '../ui/state-display.js';
import { escHtml, $ } from '../utils.js';

interface ExpiryPayload {
  token_present: boolean;
  days_remaining: number | null;
  expires_at: string | null;
  status: 'healthy' | 'info' | 'warning' | 'critical' | 'sev1';
  generated_at?: string;
  cached?: boolean;
}

let _payload: ExpiryPayload | null = null;

export async function loadCredentialsExpiry(): Promise<void> {
  const sub = $('credentials-expiry-sub');
  const root = $('credentials-expiry-root');

  // Endpoint requireAdminBearer korumalı → önce admin anahtarı sağla.
  if (!ensureAdminBearer('Kimlik-süresi paneli')) {
    if (sub) sub.textContent = '— admin yetkisi gerekli';
    if (root) renderAuthRequired(root, { onCta: () => { void loadCredentialsExpiry(); } });
    return;
  }

  if (sub) sub.textContent = 'token expiry yükleniyor...';
  if (root) renderLoading(root, { text: 'Token expiry yükleniyor...' });
  try {
    const r = await authBearerFetch('/castintech-api/credentials/expiry-status');
    if (r.status === 401 || r.status === 403) {
      clearAdminBearer(); // geçersiz/eksik bearer'ı temizle ki yeniden sorulabilsin
      if (sub) sub.textContent = '— admin yetkisi gerekli';
      if (root) renderAuthRequired(root, {
        description: 'Admin anahtarı geçersiz veya eksik. Doğru CASTINTECH_ADMIN_KEY ile tekrar deneyin.',
        onCta: () => { void loadCredentialsExpiry(); },
      });
      return;
    }
    if (!r.ok) throw new Error('expiry-status HTTP ' + r.status);
    _payload = (await r.json()) as ExpiryPayload;
    renderCredentialsExpiry();
  } catch (e) {
    if (sub) sub.textContent = 'hata: ' + (e as Error).message;
    if (root) renderError(root, e as Error, { onRetry: () => { void loadCredentialsExpiry(); } });
  }
}

export function renderCredentialsExpiry(): void {
  const sub = $('credentials-expiry-sub');
  const root = $('credentials-expiry-root');
  if (!_payload || !root) {
    if (sub) sub.textContent = '— veri yok';
    return;
  }
  const p = _payload;
  if (sub) {
    const gen = p.generated_at ? new Date(p.generated_at).toLocaleString('tr-TR') : '';
    sub.textContent = `${p.status.toUpperCase()} • ${p.days_remaining ?? '?'} gün kaldı` +
      (p.cached ? ' (cache)' : '') + (gen ? ` • ${gen}` : '');
  }

  const badgeClass = statusToBadgeClass(p.status);
  const days = p.days_remaining;
  const expiresAt = p.expires_at ? new Date(p.expires_at).toLocaleDateString('tr-TR') : '—';
  const tokenStatus = p.token_present ? 'Var' : 'YOK';

  root.innerHTML = `
    <div class="ce-card">
      <div class="ce-row">
        <span class="ce-label">Token</span>
        <span class="ce-value">${escHtml(tokenStatus)}</span>
      </div>
      <div class="ce-row">
        <span class="ce-label">Kalan gün</span>
        <span class="ce-badge ${badgeClass}">${days === null ? '?' : escHtml(String(days))}</span>
      </div>
      <div class="ce-row">
        <span class="ce-label">Expires at</span>
        <span class="ce-value ce-mono">${escHtml(expiresAt)}</span>
      </div>
      <div class="ce-row">
        <span class="ce-label">Status</span>
        <span class="ce-badge ${badgeClass}">${escHtml(p.status)}</span>
      </div>
      <div class="ce-actions">
        <button id="credentials-rotate-btn" class="ce-btn">Rotate now (manuel komut)</button>
      </div>
      <pre id="credentials-rotate-cmd" class="ce-cmd ce-hidden"># Token rotation — manuel akış (Memory: project_oauth_token_env.md):
# 1) docker exec castintech-paperclip claude setup-token
# 2) URL'yi browser'da aç, basyilmaz@gmail ile login
# 3) Callback URL'yi (code#state) PTY'ye yapıştır
# 4) Yeni token'i /opt/castintech/.env CLAUDE_CODE_OAUTH_TOKEN'a yaz
# 5) docker compose up -d --force-recreate paperclip
#
# Daemon manuel çalıştırma:
# bash /opt/castintech/scripts/credentials-rotation-daemon.sh
</pre>
    </div>
  `;
  bindRotateButton();
}

function statusToBadgeClass(status: string): string {
  switch (status) {
    case 'healthy': return 'ce-ok';
    case 'info': return 'ce-info';
    case 'warning': return 'ce-warn';
    case 'critical': return 'ce-danger';
    case 'sev1': return 'ce-sev1';
    default: return 'ce-info';
  }
}

function bindRotateButton(): void {
  const btn = document.getElementById('credentials-rotate-btn');
  const cmd = document.getElementById('credentials-rotate-cmd');
  if (btn && cmd) {
    btn.addEventListener('click', () => {
      cmd.classList.toggle('ce-hidden');
    });
  }
}

export function bindCredentialsExpiryRefresh(): void {
  const btn = document.getElementById('credentials-expiry-refresh-btn');
  if (btn) {
    btn.addEventListener('click', () => {
      void loadCredentialsExpiry();
    });
  }
}
